import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifySuccess } from 'common/utils/notifier.utils';
import {
	useCreateMerchantMutation,
	useUpdateMerchantMutation,
} from 'domains/merchant/queries/merchant.query';
import { TCreateMerchantReqData, TUpdateMerchantReqData } from 'domains/merchant/types/merchant.types';
import { SFormContainer, SFooter } from './modal.style';
import { EMerchantCreateEditForm, EModalType } from '../enums/merchants.enums';
import { TMerchantModalData } from '../types/merchants.types';

type TProps = {
	isOpen: boolean;
	onCancelModalHandler: () => void;
	data: TMerchantModalData;
};

const CreateEditMerchantsModal = (props: TProps): ReactElement => {
	const {
		isOpen,
		onCancelModalHandler,
		data: { rowData, type: modalType},
	} = props;
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

	const { mutateAsync: createMerchantMutation } = useCreateMerchantMutation();
	const { mutateAsync: updateMerchantMutation } = useUpdateMerchantMutation();

	useEffect(() => {
		if (Object.keys(rowData || {}).length) {
			const {
        customer_guid,
				name,
				company_code,
				callback_url,
				email,
			} = rowData;

			form.setFields([
        {
          name: EMerchantCreateEditForm.CustomerGUID,
          value: customer_guid,
        },
				{
					name: EMerchantCreateEditForm.Name,
					value: name,
				},
				{
					name: EMerchantCreateEditForm.CompanyCode,
					value: company_code,
				},
				{
					name: EMerchantCreateEditForm.CallbackUrl,
					value: callback_url,
				},
				{
					name: EMerchantCreateEditForm.Email,
					value: email,
				},
			]);
		}
	}, [rowData]);

	const onCloseModalHandler = (): void => {
		form.resetFields();
		onCancelModalHandler();
	};

	const onFinishFormHandler = async (formData: (Record<EMerchantCreateEditForm, string>)): Promise<void> => {
		try {
			setIsSubmitBtnLoading(true);

			const {
        customer_guid,
				name,
				company_code,
				callback_url,
				email,
			} = formData;

			if (modalType === EModalType.Edit) {
				const updateMerchantReqData: TUpdateMerchantReqData = {
					name,
					company_code,
					email,
					callback_url,
				};
				await updateMerchantMutation({
					merchantGUID: customer_guid,
					requestData: updateMerchantReqData,
				});
				notifySuccess('usrRoleMng.merchants.modal.edit.notification.success');
			} else {
				const createMerchantReqData: TCreateMerchantReqData = {
					customer_guid,
					name,
					company_code,
					email,
					callback_url,
				};
				await createMerchantMutation({
          counterpartyGUID: ENV_COUNTERPARTY_GUID,
					requestData: createMerchantReqData,
				});
				notifySuccess('usrRoleMng.merchants.modal.create.notification.success');
			}

			await queryClient.invalidateQueries({
				queryKey: queryKeysClient[EQueryKeys.MerchantQueryKeys].getMerchantsList._def,
			});

			setIsSubmitBtnLoading(false);
			onCancelModalHandler();
		} catch (error) {
			console.error(error);
			setIsSubmitBtnLoading(false);
		}
	};

	return (
		<EnhancedModal width={764} open={isOpen} onCancel={onCloseModalHandler}>
			<Form form={form} onFinish={onFinishFormHandler}>
				<Header>
					{modalType === EModalType.Edit
						? t('usrRoleMng.merchants.modal.edit.title')
						: t('usrRoleMng.merchants.modal.create.title')}
				</Header>

				<SFormContainer>
          <Form.Item
            name={EMerchantCreateEditForm.CustomerGUID}
            label={t('usrRoleMng.merchants.modal.field.customerGuid.label')}
          >
            <Input
              placeholder={t('usrRoleMng.merchants.modal.field.customerGuid.placeholder') as string}
              size="large"
            />
          </Form.Item>
					<Form.Item
						name={EMerchantCreateEditForm.Name}
						label={t('usrRoleMng.merchants.modal.field.name.label')}
					>
						<Input
							placeholder={t('usrRoleMng.merchants.modal.field.name.placeholder') as string}
							size="large"
						/>
					</Form.Item>
					<Form.Item
						name={EMerchantCreateEditForm.CompanyCode}
						label={t('usrRoleMng.merchants.modal.field.companyName.label')}
					>
						<Input
							placeholder={
								t('usrRoleMng.merchants.modal.field.companyName.placeholder') as string
							}
							size="large"
						/>
					</Form.Item>
					<Form.Item
						name={EMerchantCreateEditForm.CallbackUrl}
						label={t('usrRoleMng.merchants.modal.field.callbackUrl.label')}
					>
						<Input
							placeholder={
								t('usrRoleMng.merchants.modal.field.callbackUrl.placeholder') as string
							}
							size="large"
						/>
					</Form.Item>
					<Form.Item
						name={EMerchantCreateEditForm.Email}
						label={t('usrRoleMng.merchants.modal.field.email.label')}
					>
						<Input
							placeholder={t('usrRoleMng.merchants.modal.field.email.placeholder') as string}
							size="large"
						/>
					</Form.Item>
				</SFormContainer>

				<Footer>
					<SFooter>
						<Button style={{ marginRight: 12 }} onClick={onCloseModalHandler}>
							{t('usrRoleMng.merchants.modal.button.cancel')}
						</Button>
						<Button loading={isSubmitBtnLoading} htmlType="submit" type="primary">
							{modalType === EModalType.Edit
								? t('usrRoleMng.merchants.modal.button.edit')
								: t('usrRoleMng.merchants.modal.button.create')}
						</Button>
					</SFooter>
				</Footer>
			</Form>
		</EnhancedModal>
	);
};

export default CreateEditMerchantsModal;
